import type { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { ArrayItemObject, ArticleResponse, ArticleType, HeaderData, ResourceData, ResourceListData, StrapiResourceList, StrapiResponse } from '../../../utils/types'
import WebsiteLayout from '../../../components/layout/WebsiteLayout'
import { fetchAPI } from '../../../utils/api'
import ArticleModule from '../../../components/sections/ArticleModule'
import NextImage from "next/image"
import parse from 'html-react-parser';
import {useState} from 'react'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import LoadingScreen from '../../LoadingScreen'
import NotFound from '../../NotFound'
import ArticleCard from '../../../components/cards/ArticleCard'
import NewsLetter from '../../../components/sections/NewsLetter'

const ImageEndpoint = process.env.NEXT_PUBLIC_UPLOADS_URL

interface PageProps {
  article: ResourceData,
  preview: boolean,
  relatedArticles: Array<ArrayItemObject<ResourceData>>,
  recentArticles: Array<ArrayItemObject<ResourceData>>
}

const header = {
  id: 1,
  __component: "component.header",
  title: "",
  subtitle: "",
  theme: "green",
  headerType: "hidden"
} as HeaderData

const ResourcePage: NextPage<PageProps> = ({ article, preview, relatedArticles, recentArticles }) => {

  const [paddingTop, setPaddingTop] = useState('0');

  const articleModule = {
    category: `More from ${article?.category?.data?.attributes.title}`,
    theme: 'green',
    resources: {
      data: relatedArticles?.slice(0, 3)
    }
  } as ResourceListData
  
  const router = useRouter()

    if (!router.isFallback && (article === undefined || article === null)) {
        return (
            <WebsiteLayout
                navbarTheme={'white'}
                header={header}
                preview={false}
                secondaryNavbar={undefined}
            >
                <NotFound />
            </WebsiteLayout>
        )
    }

    if (router.isFallback) {
        return (
            <LoadingScreen />
        )
    }

  const options = {
    replace: (node: any) => {
  
      if (node.name === 'figure') {
        const img = node.children[0]
        // TODO handle cases where the text withing figcaption is nested in other elements
        const imgSrc = `${ImageEndpoint}${img.attribs.src}`

        let caption = ''

        const getCaptions = (mlem: any) => {
          if (mlem.type === 'text') {
            caption = mlem.data
          } else if ( mlem.children && mlem.children.length ) {
            getCaptions(mlem.children[0])
          } else {
            return
          }
        }

        node.children.length > 1 && getCaptions(node.children[1])

        //node.children.length > 1 ? console.log(getCaptions(node.children[1])) : console.log('doesn\'t have it')

        const myImage = <NextImage src={imgSrc} alt={caption} 
          layout="fill" 
          objectFit="contain" 
          onLoad={({ target }) => {
            const { naturalWidth, naturalHeight, width, height } = target as HTMLImageElement;
            setPaddingTop(`calc(100% / (${naturalWidth <= 1 ? width : naturalWidth} / ${naturalHeight <= 1 ? height : naturalHeight})`);
          }}
        />
  
        return (
          <figure className="image">
            <div className="image_resized" style={{ paddingTop }}>
              {/*
              We don't get width and height from the images in the blog so we 
              use onload on the image the get the dimensions and calculate the appropriate padding
              for the container.
              https://stackoverflow.com/a/71096113 

              This breaks avoiding layout shifts when the image is loaded, but at least we can use next/image 
              image optimizations  feature
              */}
              {myImage}
            </div>
            {(caption && caption !== '') && <figcaption>{ parse(caption)}</figcaption>}
          </figure>
        )
      }
    }
  }

  const content = parse(article.description, options)

  const placeholderDescription = 'Polymath Ventures is a company builder creating and scaling human-centered businesses to empower the middle-class in Latin America.'

  return (
    <>
      <NextSeo
        title={article.metaData?.metaTitle || article.title}
        description={article.metaData?.metaDescription || placeholderDescription}
        openGraph={{
          title: article.metaData?.metaTitle || article.title,
          description: article.metaData?.metaDescription || placeholderDescription,
          images: article.metaData?.shareImage?.data ? [
            {
              url: `${ImageEndpoint}${article.metaData.shareImage.data.attributes.url}`
            }
          ] : undefined
        }}
      />
      <WebsiteLayout header={header} navbarTheme='green' preview={preview} footer={null}>
        <div className="grid grid-cols-10 bg-white w-full article overflow-visible">
          <div className='col-span-10 flex flex-col-reverse md:flex-row md:h-[calc(100vh_-_3.8rem)]'>
            <div className='article-header w-full md:w-[50%] xl:w-[40%] flex flex-col justify-end gap-10 p-5 pb-5 md:p-10 lg:p-16 md:pb-8'>
              {article.consumptionTime &&
                <span className='ml-4 font-bold text-primary-green'>
                  {article.consumptionTime} minutes read
                </span>
              }
              {article.authors?.data && 
                (article.authors.data.length <= 2 ?
                  <div className='flex flex-col gap-3'>
                    {article.authors?.data.map((item, index) => 
                      <Link key={index} href={'/team/' + item.attributes.slug} passHref>
                        <a className='flex items-center gap-3'>
                          {item.attributes.photo && 
                            <div className='relative h-[3rem] w-[3rem] overflow-hidden rounded-full'>
                              <NextImage
                                src={`${ImageEndpoint}${item.attributes.photo.data.attributes.url}`}
                                alt=""
                                layout="fill"
                                objectFit='cover'
                              />
                            </div>
                          }
                          <div className='flex flex-col'>
                            <span className='font-bold text-[1.3rem]'>{item.attributes.name}</span>
                            <span className='text-[0.9rem]'>{item.attributes.jobTitle}</span>
                          </div>
                        </a>
                      </Link>)
                    }
                  </div>
                  :
                  <div className='flex flex-row gap-3'>
                    {article.authors?.data.map((item, index) => 
                      <Link key={index} href={'/team/' + item.attributes.slug} passHref>
                        <a className='flex items-center gap-3'>
                          {item.attributes.photo && 
                            <div className='relative h-[3rem] w-[3rem] md:h-[4rem] md:w-[4rem] overflow-hidden rounded-full'>
                              <NextImage
                                src={`${ImageEndpoint}${item.attributes.photo.data.attributes.url}`}
                                alt=""
                                layout="fill"
                                objectFit='cover'
                              />
                            </div>
                          }
                        </a>
                      </Link>)
                    }
                  </div>
                )
              }
              {article.title &&
                <div>
                  <h1 className='font-bold text-[clamp(1.6rem,3vw,2.4rem)] text-stone-700'>
                    {article.title}
                  </h1>
                  <span className="uppercase font-medium text-gray-500 w-fit" style={{ borderBottom: 'solid 3px #49C5B1' }}>
                    {article.creationDate}
                  </span>
                </div>
              }
              {article.metaData?.metaDescription ? 
                <div 
                  className='text-[1rem] md:text-[1.1rem] lg:text-[1.2rem] xl:text-[1.3rem]' 
                  dangerouslySetInnerHTML={{__html: article.metaData.metaDescription}}></div> 
                : 
                undefined
              }
            </div>
            <div className="relative aspect-[2] md:aspect-auto md:h-full w-full md:w-[50%] xl:w-[60%]">
              {article.headerImage.data &&
                <NextImage
                  priority
                  className="header-image"
                  src={`${ImageEndpoint}${article.headerImage.data.attributes.url}`}
                  alt=""
                  layout="fill"
                  objectFit='cover'
                  role="presentation"
                />
              }
            </div>
          </div>
          
          <div className='col-span-5 xl:col-span-4 md:px-10 xl:px-16 md:py-10 recents h-fit'>
            <RecentArticles data={recentArticles} tag={article.category?.data?.attributes?.title || ''} />
          </div>
          <div className='article-content p-5 md:pr-10 xl:pr-16 md:pt-10 md:pl-0 col-span-10 md:col-span-5 xl:col-span-6 flex flex-col font-medium text-stone-700 text-lg' >
            {content}
            {article.externalLinks.length ?
              <div className={`flex justify-center ${content ? 'py-10' : ''}`}>
                {article.externalLinks.map((item, index) => (
                  <iframe
                    key={index}
                    src={item.URL}
                    width={item.Icon === 'Youtube' ? "560" : "700"}
                    height={item.Icon === 'Youtube' ? "315" : "280"}
                    frameBorder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                  >
                  </iframe>
                ))}
              </div>
              : 
              null
            }
          </div>
        </div>
        {(articleModule && articleModule.resources.data && articleModule.resources.data.length) ?
          <ArticleModule data={articleModule} />
          :
          null
        }
      </WebsiteLayout>
    </>
  )
}

export const getStaticProps: GetStaticProps= async (context) => {

  let article = null
  let recentArticles = null
  let relatedArticles = null

  const query = {
    filters: {
      slug: {
          // TODO figure out if there is a case where slug isn't passed as string as per type def.
          $eq: (context.params?.slug as [] || [""])
      }
    },
    populate: {
      authors: {
        populate: '*'
      },
      media: {
        url: true,
        alternativeText: true,
        ext: true,

      },
      externalLinks: {
        populate: '*'
      },
      metaData: {
        populate: '*'
      },
      thumbnail: {
        url: true,
        alternativeText: true
      },
      headerImage: {
        url: true,
        alternativeText: true
      },
      category: {
        populate: '*'
      }
    }
  }

  const response = await fetchAPI<StrapiResponse<ArticleResponse>>('resources', query)

  // TODO handle lenght != 1

  article = response.data[0]?.attributes || null

  const preview = context.preview || null

  const recentArticlesQuery = {
    filters: {
      type: {
        $in: ['Blog', 'Video', 'Audio']
      },
      creationDate: {
        $notNull: true
      }
    },
    sort: {
      creationDate: 'desc'
    },
    pagination: {
      start: 0,
      limit: 3
    },
    populate: {
      metaData: {
        populate: '*'
      },
      thumbnail: {
        url: true,
        alternativeText: true
      },
      authors: {
        select: ['name', 'photo', 'slug']
      },
      category: {
        populate: '*'
      }
    }
  }

  if ( article ) {
    const relatedArticlesQuery = {
      filters: {
        category: {
          title: {
            $eq: article.category.data ? article.category.data.attributes.title : null
          }
        },
        type: {
          $in: ['Blog', 'Video', 'Audio']
        },
        creationDate: {
          $notNull: true
        }
      },
      pagination: {
          start: 0,
          limit: 3
      },
      populate: {
        authors: {
          populate: '*'
        },
        externalLinks: {
          populate: '*'
        },
        metaData: {
          populate: '*'
        },
        thumbnail: {
          url: true,
          alternativeText: true
        },
        category: {
          populate: '*'
        }
      }
    }
    const responseRelated = await fetchAPI<StrapiResponse<ArticleResponse>>('resources', relatedArticlesQuery)

    relatedArticles = responseRelated.data || null
  }


  const responseRecent  = await fetchAPI<StrapiResponse<ArticleResponse>>('resources', recentArticlesQuery)

  recentArticles = responseRecent.data || null

  return {
      props: {
        article,
        preview,
        relatedArticles,
        recentArticles
      }
  }
}

export async function getStaticPaths(locale:any) {
  const articlesQuery = {
    filters: {
      type: {
        $in: ['Blog', 'Video', 'Audio']
      },
    },
    pagination: {
        start: 0,
        limit: 150
    }
  }

  let results = await fetchAPI<StrapiResponse<ArticleResponse>>('resources', articlesQuery)
  
  let paths = results.data.map((page:any) => {
      if(!page || !page.attributes){
        return
      }
      let slug = page.attributes.slug.split('/')[0]
      return {
          params: {
              ...page.attributes,
              slug: slug,
              id: page.id
          },
      }
  })

  return {
      paths: paths,
      fallback: true
  };
}

export default ResourcePage

interface RecentArticlesProps {
  data: Array<ArrayItemObject<ResourceData>>,
  tag: string
}

const RecentArticles = ({ data, tag }: RecentArticlesProps) => {

  function dateSort(a: ArrayItemObject<ResourceData>, b: ArrayItemObject<ResourceData>) {
    const dateA = a.attributes.creationDate// || a.publishedAt || a.createdAt
    const dateB = b.attributes.creationDate// || b.publishedAt || b.createdAt
    if(dateA == dateB){
      return 0
    }
    return dateA < dateB ? 1 : -1
  }

  const getLatests = () => {
    return data?.filter(x => x &&  x.attributes.creationDate)
      .sort(dateSort).map(item => item && item.attributes)
  }

  return (
    <div className='flex flex-col gap-5'>
      {(tag.toLowerCase() === 'engineering' || tag.toLowerCase() === 'tech') ?
        <div className='w-full bg-primary-blue py-8 px-5 rounded-lg shadow-lg flex flex-col gap-5'>
          <h3 className='text-white text-[1.8rem] font-[700]'>Join our Tech newsletter</h3>
          <NewsLetter actionUrl='https://polymathv.us3.list-manage.com/subscribe/post?u=bf424acd364ec846df798dafc&amp;id=3040299f44&amp;f_id=0040d0e1f0' />
        </div>
        :
        null
      }
      <h4 className='text-[1.8rem] font-bold text-primary-blue'>Recent articles</h4>
      <div className='flex flex-col gap-10 max-h-[80vh] recents-list pr-3'>
        {getLatests()?.length && getLatests().slice(0, 3).map((item, index) =>(
          <ArticleCard key={index} type={ArticleType.REDUCED} leanTitle={true} data={item} />
        ))}
      </div>
    </div>
  )
}
